import {
  IconCloudSlashFill18,
  IconRefreshAnticlockwiseFill18,
  IconWifiOffFill18,
  IconWrenchScrewdriverFill18,
} from "nucleo-ui-fill-18"
import type * as React from "react"
import { useEffect } from "react"
import { createPortal } from "react-dom"

import { EmptyState } from "@/components/common/empty-state"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import { useOutage } from "@/hooks/use-outage"
import { retryNow, type Outage } from "@/lib/api/outage"
import { cn } from "@/lib/utils"

const COPY: Record<
  Outage,
  { icon: React.ReactNode; title: string; description: string }
> = {
  unreachable: {
    icon: <IconCloudSlashFill18 />,
    title: "We can't reach the server",
    description: "We'll keep trying and carry on where you left off.",
  },
  offline: {
    icon: <IconWifiOffFill18 />,
    title: "You're offline",
    description: "We'll reconnect as soon as you're back online.",
  },
  maintenance: {
    icon: <IconWrenchScrewdriverFill18 />,
    title: "Down for maintenance",
    description: "We'll carry on as soon as it's finished.",
  },
}

/**
 * Covers the whole application while the API cannot answer, rather than
 * leaving every block on the page to fail on its own. The page underneath
 * stays mounted, so a half-filled form is still there when the API returns.
 */
function ServiceUnavailable({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const { outage, isChecking } = useOutage()

  useEffect(() => {
    const root = document.getElementById("root")

    if (!root || !outage) {
      return
    }

    root.inert = true

    return () => {
      root.inert = false
    }
  }, [outage])

  if (!outage) {
    return null
  }

  const { icon, title, description } = COPY[outage]

  return createPortal(
    <div
      data-slot="service-unavailable"
      role="alertdialog"
      aria-modal="true"
      aria-label={title}
      className={cn(
        "fixed inset-0 z-[100] flex flex-col bg-background",
        className
      )}
      {...props}
    >
      <EmptyState
        icon={icon}
        title={title}
        description={description}
        aria-live="polite"
        actions={
          <Button
            autoFocus
            variant="outline"
            size="sm"
            disabled={isChecking}
            onClick={retryNow}
          >
            {isChecking ? (
              <Spinner data-icon="inline-start" />
            ) : (
              <IconRefreshAnticlockwiseFill18 data-icon="inline-start" />
            )}
            Try again
          </Button>
        }
      />
    </div>,
    document.body
  )
}

export { ServiceUnavailable }
